/**
 * Mappature placeholder usate dalla modale NewMessages per i template damage di Notification Hub.
 *
 * Estratte 1:1 da NewMessages.tsx (LPP-20764) per ridurre la dimensione del componente.
 * Eventuali riformulazioni (riduzione duplicazione token N/A_*, i18n via templates.json,
 * server-driven meta) sono valutate come step successivo.
 */

export interface PlaceholderMeta {
  label: string;
  required: boolean;
  editable: boolean;
}

export interface TemplatePlaceholder {
  eventParam: string;
  templateParam: string;
  paramType?: string;
}

export interface PlaceholderValidationMessages {
  requiredPlaceholder: string;
  invalidAmount: string;
  invalidEmail: string;
}

/**
 * Metadati per ogni placeholder NH usato nei template damage.
 * - label: etichetta mostrata nell'editor placeholder
 * - required: il send fallisce se vuoto
 * - editable: l'operatore può modificarlo nell'UI; i non-editable sono solo preview
 *
 * Note:
 * - TemplateOperatorMail è opzionale e viene mostrato solo se presente nel template NH attivo.
 * - TemplateManDmsMail è backend-owned: il FE può riceverlo/renderizzarlo, ma non deve renderlo editabile.
 * - TemplateDest è la chiave allineata a operation (MailTemplateParameter.DEST) e NH, NON "TemplateDestination".
 */
export const PLACEHOLDER_META: Record<string, PlaceholderMeta> = {
  TemplateFormId: { label: "Codice scheda", required: false, editable: false },
  TemplateFormStatus: { label: "Stato scheda", required: false, editable: false },
  TemplateVehiclePlate: { label: "Targa veicolo", required: false, editable: false },
  TemplateVehicleModel: { label: "Modello veicolo", required: false, editable: false },
  TemplateInsNumber: { label: "Nr. sinistro assicurativo", required: false, editable: false },
  TemplateCounterpart: { label: "Controparte", required: false, editable: false },
  TemplateCpPhone: { label: "Telefono controparte", required: false, editable: false },
  TemplateCpAddress: { label: "Indirizzo controparte", required: false, editable: false },
  TemplateCpVehicleModel: { label: "Veicolo controparte", required: false, editable: false },
  TemplateCpVehiclePlate: { label: "Targa controparte", required: false, editable: false },
  TemplateCpInsCompany: { label: "Compagnia ass. controparte", required: false, editable: false },
  TemplateAccDate: { label: "Data sinistro", required: false, editable: false },
  TemplateAccPlace: { label: "Luogo sinistro", required: false, editable: false },
  TemplateAccProv: { label: "Provincia sinistro", required: false, editable: false },
  TemplateCurrency: { label: "Valuta", required: false, editable: false },
  TemplateRaNumber: { label: "Numero noleggio", required: false, editable: false },
  TemplateManDmsName: { label: "Nome DMS gestione", required: false, editable: false },
  TemplateManDmsPhone: { label: "Telefono DMS gestione", required: false, editable: false },
  TemplateManDmsAddress: { label: "Indirizzo DMS gestione", required: false, editable: false },
  TemplateManDmsCap: { label: "CAP DMS gestione", required: false, editable: false },
  TemplateManDmsMail: { label: "Email DMS gestione", required: false, editable: false },
  TemplateRequestedAmount: { label: "Importo richiesto", required: true, editable: true },
  TemplateReportType: { label: "Tipo verbale", required: true, editable: true },
  TemplateOperatorMail: { label: "Email operatore", required: false, editable: true },
  TemplateSource: { label: "Partenza", required: true, editable: true },
  TemplateDest: { label: "Arrivo", required: true, editable: true },
};

export const AMOUNT_PLACEHOLDERS = new Set(["TemplateRequestedAmount"]);
export const EMAIL_PLACEHOLDERS = new Set(["TemplateOperatorMail"]);

const AMOUNT_PATTERN = /^\d+([,.]\d{1,2})?$/;
const EMAIL_PATTERN = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

export const validateEditablePlaceholder = (
  templateParam: string,
  value: string | undefined,
  messages: PlaceholderValidationMessages
): string | undefined => {
  const meta = PLACEHOLDER_META[templateParam];
  const trimmedValue = value?.trim() || "";

  if (meta?.required && !trimmedValue) {
    return messages.requiredPlaceholder;
  }

  if (!trimmedValue) {
    return undefined;
  }

  if (AMOUNT_PLACEHOLDERS.has(templateParam) && !AMOUNT_PATTERN.test(trimmedValue)) {
    return messages.invalidAmount;
  }

  if (EMAIL_PLACEHOLDERS.has(templateParam) && !EMAIL_PATTERN.test(trimmedValue)) {
    return messages.invalidEmail;
  }

  return undefined;
};

/**
 * Fallback tecnici per dati mancanti, allineati ai defaultNA del backend OPR Damage.
 * Non sono traduzioni UI: sono valori legacy mostrati quando il dato scheda non è disponibile.
 */
export const MISSING_DATA_TOKEN_BY_PARAM: Record<string, string> = {
  TemplateFormStatus: "N/A_FORM_STATUS",
  TemplateCounterpart: "N/A_COUNTERPART",
  TemplateCpPhone: "N/A_CP_PHONE",
  TemplateCpAddress: "N/A_CP_ADDRESS",
  TemplateCpVehicleModel: "N/A_CP_VEHICLE_MODEL",
  TemplateCpVehiclePlate: "N/A_CP_VEHICLE_PLATE",
  TemplateCpInsCompany: "N/A_CP_INS_COMPANY",
  TemplateAccDate: "N/A_ACC_DATE",
  TemplateAccPlace: "N/A_ACC_PLACE",
  TemplateAccProv: "N/A_ACC_PROV",
  TemplateCurrency: "N/A_CURRENCY",
  TemplateManDmsName: "N/A_MAN_DMS_NAME",
  TemplateManDmsPhone: "N/A_MAN_DMS_PHONE",
  TemplateManDmsAddress: "N/A_MAN_DMS_ADDRESS",
  TemplateManDmsCap: "N/A_MAN_DMS_CAP",
  TemplateManDmsMail: "N/A_MAN_DMS_MAIL",
  TemplateInsNumber: "N/A_INS_NUMBER",
};

/**
 * Marker legacy localizzati presenti nei template mail per valori compilati dall'operatore.
 * Servono per riconoscere/sostituire template legacy; non sono la fonte i18n ideale della UI.
 */
export const LEGACY_EDITABLE_MARKERS_BY_PARAM: Record<string, Record<string, string>> = {
  TemplateRequestedAmount: { it: "[inserire importo]", en: "[insert amount]", fr: "[insérer montant]", es: "[insertar importe]", pt: "[introduza o valor]" },
  TemplateReportType:      { it: "[inserire tipo verbale]", en: "[insert report type]", fr: "[insérer type de procès-verbal]", es: "[tipo verbal]", pt: "[inserir tipo de relatório]" },
  TemplateOperatorMail:    { it: "[inserire mail operatore]", en: "[insert operator email]", fr: "[insérer l'e-mail de l'opérateur]", es: "[insertar correo electrónico del operador]", pt: "[inserir e-mail do operador]" },
  TemplateSource:          { it: "[partenza]", en: "[departure]", fr: "[départ]", es: "[salida]", pt: "[partida]" },
  TemplateDest:            { it: "[arrivo]", en: "[arrival]", fr: "[arrivée]", es: "[llegada]", pt: "[chegada]" },
};

/**
 * Mappa compatibile templateParam -> token da mostrare nella preview.
 * Mantiene invariato il comportamento esistente, ma deriva da due famiglie distinte.
 */
export const PLACEHOLDER_DISPLAY_TOKEN: Record<string, Record<string, string>> = {
  ...Object.fromEntries(
    Object.entries(MISSING_DATA_TOKEN_BY_PARAM).map(([param, token]) => [
      param,
      { it: token, en: token, fr: token, es: token, pt: token },
    ])
  ),
  ...LEGACY_EDITABLE_MARKERS_BY_PARAM,
};

/** Lista flat di tutti i marker legacy per ogni param (usata da inferredPlaceholders). */
export const LEGACY_TOKEN_BY_PARAM: Record<string, string[]> = {
  ...Object.fromEntries(
    Object.entries(MISSING_DATA_TOKEN_BY_PARAM).map(([param, token]) => [param, [token]])
  ),
  ...Object.fromEntries(
    Object.entries(LEGACY_EDITABLE_MARKERS_BY_PARAM).map(([param, byLang]) => [
      param,
      Object.values(byLang),
    ])
  ),
};

/** Dato un templateParam e la lingua del template, restituisce il token OPR di fallback. */
export const getDisplayToken = (templateParam: string, lang: string): string | undefined => {
  const byLang = PLACEHOLDER_DISPLAY_TOKEN[templateParam];
  if (!byLang) return undefined;
  return byLang[lang] || byLang["it"];
};

/**
 * Compila il template per la preview nella modale.
 */
export const renderTemplate = (
  template: string,
  values: Record<string, string>,
  lang: string
): string => {
  let result = template || "";
  const templateLang = lang || "it";

  Object.entries(values).forEach(([key, value]) => {
    const hasValue = value !== undefined && value !== null && value !== "";
    if (hasValue) {
      result = result.replaceAll(`{{${key}}}`, value);
      (LEGACY_TOKEN_BY_PARAM[key] || []).forEach((token) => {
        result = result.replaceAll(token, value);
      });
    } else {
      const fallback = getDisplayToken(key, templateLang) || `{{${key}}}`;
      result = result.replaceAll(`{{${key}}}`, fallback);
    }
  });

  Object.keys(PLACEHOLDER_DISPLAY_TOKEN).forEach((templateParam) => {
    const nhToken = `{{${templateParam}}}`;
    if (result.includes(nhToken)) {
      const fallback = getDisplayToken(templateParam, templateLang) || nhToken;
      result = result.replaceAll(nhToken, fallback);
    }
  });

  return result;
};

/**
 * Inferisce i placeholder presenti in subject/body quando NH non restituisce
 * event_template_association_parameter per l'eventType.
 */
export const inferTemplatePlaceholders = (source: string): TemplatePlaceholder[] => {
  const inferred: TemplatePlaceholder[] = [];

  Object.entries(LEGACY_TOKEN_BY_PARAM).forEach(([templateParam, tokens]) => {
    if (tokens.some((token) => source.includes(token))) {
      inferred.push({ eventParam: templateParam, templateParam });
    }
  });

  Object.keys(PLACEHOLDER_META).forEach((templateParam) => {
    const token = `{{${templateParam}}}`;
    if (source.includes(token) && !inferred.some((p) => p.templateParam === templateParam)) {
      inferred.push({ eventParam: templateParam, templateParam });
    }
  });

  return inferred;
};
